import { CheckIcon, XMarkIcon } from "@heroicons/react/24/outline";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Collapse,
  Group,
  List,
  Loader,
  Stack,
  Text,
  Tooltip,
  useMantineColorScheme,
  useMantineTheme
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import React, { useEffect, useState } from "react";
import { CVItemCard } from "../../../../../components/common/CVItemCard";
import { CVSectionContainer } from "../../../../../components/common/CVSectionContainer";
import { DeleteConfirmationModal } from "../../../../../components/common/DeleteConfirmationModal";
import { FeatureUnavailableBanner } from "../../../../../components/common/FeatureUnavailableBanner";
import { MonthYearDateInput } from "../../../../../components/common/MonthYearDateInput";
import { ValidationResult } from "../../../../../components/common/ValidationResult";
import { FormUIColor } from "../../../../../constants/colorPalette";
import { useEditMode } from "../../../../../context/EditModeContext";
import { useCertificatesDictionary } from "../../../../../hooks/queries/useCertificatesDictionary";
import { useFeatureFlags } from "../../../../../hooks/queries/useFeatureFlags";
import { ValidateableCvSections } from "../../../../../types/cvSections";
import { formatDateRange, toLocalDateString, toUTCDate } from "../../../../../utils/dateFormat";
import { Certification, CertificationViewModel } from "../models/certification.model";
import { PersonCertificationService } from "../services/personCertificationService";

const INSTRUCTIONS = (
  <Stack gap={5}>
    <Text fw={500}>Instructions from your company</Text>
    <Text size="sm">
      Here you add all the certifications you have received.
    </Text>
    <Text size="sm" mt={5}>
      - Date
    </Text>
    <Text size="sm">- Validity period</Text>
    <Text size="sm">- The name of the certification</Text>
    <Text size="sm">- Attach copy!</Text>
  </Stack>
);

interface CertificationsProps {
  certifications: CertificationViewModel[];
  onChange: (certifications: CertificationViewModel[]) => void;
  personId?: number;
  cvId?: number;
}

export function Certifications({
  certifications,
  onChange,
  personId,
  cvId,
}: CertificationsProps) {
  const { isEditMode } = useEditMode();
  const theme = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === 'dark';
  const { data: featureFlags } = useFeatureFlags();
  const certificateManagementEnabled = featureFlags?.enableCertificateManagement ?? true;
  const [isAddFormOpen, setIsAddFormOpen] = useState(false);
  const [editingCertId, setEditingCertId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Function to format certifications data for LLM validation
  const getCertificationsSectionData = (): string => {
    if (!certifications || certifications.length === 0) {
      return 'No certifications found.';
    }

    return certifications.map((cert, index) => {
      return `
Certification ${index + 1}:
- Name: ${cert.name}
- Start Date: ${cert.startDate || 'Not specified'}
- Expiry Date: ${cert.expiryDate || 'No expiry'}
      `.trim();
    }).join('\n\n');
  };

  // No longer need separate month/year fields, just the Date objects
  const [newCertification, setNewCertification] = useState<{
    name: string;
    certificateId?: number; // ID from dictionary
    startDate: Date | null;
    expiryDate: Date | null;
  }>({
    name: "",
    certificateId: undefined,
    startDate: null,
    expiryDate: null,
  });

  const [editCertification, setEditCertification] = useState<{
    name: string;
    certificateId?: number; // ID from dictionary
    startDate: Date | null;
    expiryDate: Date | null;
  }>({
    name: "",
    certificateId: undefined,
    startDate: null,
    expiryDate: null,
  });

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [certificationToDelete, setCertificationToDelete] =
    useState<CertificationViewModel | null>(null);
  const [showAddValidation, setShowAddValidation] = useState(false);
  const [showEditValidation, setShowEditValidation] = useState(false);
  const personCertificationService = new PersonCertificationService();

  // Close and reset all forms when edit mode is disabled
  useEffect(() => {
    if (!isEditMode) {
      setIsAddFormOpen(false);
      setEditingCertId(null);
      setNewCertification({
        name: "",
        certificateId: undefined,
        startDate: null,
        expiryDate: null,
      });
      setDeleteModalOpen(false);
      setShowAddValidation(false);
      setShowEditValidation(false);
    }
  }, [isEditMode]);

  // Get certificate dictionary for autocomplete
  const { data: certificateDictionary, isLoading: isDictionaryLoading } =
    useCertificatesDictionary();

  // Format certificate dictionary for autocomplete
  const certificateOptions =
    certificateDictionary?.map((cert) => ({
      value: cert.title,
      label: cert.title,
      id: cert.id,
    })) || [];

  // Load certifications from the service if personId is provided
  useEffect(() => {
    if (personId && isEditMode) {
      loadCertifications();
    }
  }, [personId, isEditMode]);

  const loadCertifications = async () => {
    if (!personId) return;

    setIsLoading(true);
    try {
      const data =
        await personCertificationService.getCertificationListByPersonId(personId);

      // Transform service data to match CertificationViewModel
      const transformedCertifications: CertificationViewModel[] = data.map((item) => {
        // Use UTC methods to extract date parts to avoid timezone issues
        const startDate = new Date(item.date);
        const startDateStr = `${startDate.getUTCFullYear()}-${String(startDate.getUTCMonth() + 1).padStart(2, '0')}-${String(startDate.getUTCDate()).padStart(2, '0')}`;

        let expiryDateStr = "";
        if (item.expirationDate) {
          const expiryDate = new Date(item.expirationDate);
          expiryDateStr = `${expiryDate.getUTCFullYear()}-${String(expiryDate.getUTCMonth() + 1).padStart(2, '0')}-${String(expiryDate.getUTCDate()).padStart(2, '0')}`;
        }

        return {
          id: item.id.toString(),
          startDate: startDateStr,
          expiryDate: expiryDateStr,
          name: item.name,
          isStarred: item.isStarred,
        };
      });

      onChange(transformedCertifications);
    } catch (error) {
      console.error("Error loading certifications:", error);
      notifications.show({
        title: "Error",
        message: "Failed to load certifications. Please try again.",
        color: "red",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddCertification = async () => {
    setShowAddValidation(true);
    if (!newCertification.name || !newCertification.startDate) return;

    const certification: CertificationViewModel = {
      id: Date.now().toString(),
      startDate: toLocalDateString(newCertification.startDate),
      expiryDate: newCertification.expiryDate
        ? toLocalDateString(newCertification.expiryDate)
        : "",
      name: newCertification.name,
      isStarred: false,
    };

    if (personId) {
      setIsSaving(true);
      try {
        const serviceData: Certification = {
          id: 0, // Will be assigned by the server
          // Backend contract uses legacy field name userId, but value is personId.
          userId: personId,
          name: certification.name,
          date: toUTCDate(newCertification.startDate),
          isStarred: certification.isStarred,
          expirationDate: newCertification.expiryDate ? toUTCDate(newCertification.expiryDate) : null,
        };

        const result = await personCertificationService.addCertificationItem(
          serviceData
        );

        // Update with the ID from the server
        certification.id = result.id.toString();

        onChange([...certifications, certification]);
        notifications.show({
          title: "Success",
          message: "Certification added successfully",
          color: "green",
        });
      } catch (error) {
        console.error("Error adding certification:", error);
        const backendMessage = (error as { responseBody?: { error?: string } })?.responseBody?.error;
        notifications.show({
          title: "Error",
          message: backendMessage ?? "Failed to add certification. Please try again.",
          color: "red",
        });
      } finally {
        setIsSaving(false);
      }
    } else {
      // Just update local state if personId is not provided (fallback)
      onChange([...certifications, certification]);
    }

    setNewCertification({
      name: "",
      certificateId: undefined,
      startDate: null,
      expiryDate: null,
    });
    setIsAddFormOpen(false);
    setShowAddValidation(false);
  };

  // Helper functions for certificate selection
  const handleNewCertificateSelect = (certificateName: string) => {
    const selectedCert = certificateOptions.find(
      (cert) => cert.label === certificateName || cert.value === certificateName
    );

    setNewCertification({
      ...newCertification,
      name: certificateName,
      certificateId: selectedCert?.id,
    });
  };

  const handleEditCertificateSelect = (certificateName: string) => {
    const selectedCert = certificateOptions.find(
      (cert) => cert.label === certificateName || cert.value === certificateName
    );

    setEditCertification({
      ...editCertification,
      name: certificateName,
      certificateId: selectedCert?.id,
    });
  };

  const handleRemoveCertification = async (id: string) => {
    if (personId) {
      const certToDelete = certifications.find((cert) => cert.id === id);
      if (!certToDelete) return;

      setIsSaving(true);
      try {
        await personCertificationService.deleteCertificationItem({
          id: parseInt(certToDelete.id),
          // Backend contract uses legacy field name userId, but value is personId.
          userId: personId,
          name: certToDelete.name,
          date: new Date(certToDelete.startDate),
          isStarred: certToDelete.isStarred,
          expirationDate: certToDelete.expiryDate
            ? new Date(certToDelete.expiryDate)
            : null,
        } as Certification);

        onChange(certifications.filter((cert) => cert.id !== id));
        notifications.show({
          title: "Success",
          message: "Certification deleted successfully",
          color: "green",
        });
      } catch (error) {
        console.error("Error deleting certification:", error);
        notifications.show({
          title: "Error",
          message: "Failed to delete certification. Please try again.",
          color: "red",
        });
      } finally {
        setIsSaving(false);
      }
    } else {
      // Just update local state if personId is not provided (fallback)
      onChange(certifications.filter((cert) => cert.id !== id));
    }
  };

  const handleEditCertification = (certification: CertificationViewModel) => {
    const startDate = certification.startDate
      ? new Date(certification.startDate)
      : null;
    const expiryDate = certification.expiryDate
      ? new Date(certification.expiryDate)
      : null;

    setEditingCertId(certification.id);
    setEditCertification({
      name: certification.name,
      certificateId: undefined, // Will be set if user changes the name
      startDate,
      expiryDate,
    });
  };

  const cancelEditCertification = () => {
    setEditingCertId(null);
    setShowEditValidation(false);
    setEditCertification({
      name: "",
      certificateId: undefined,
      startDate: null,
      expiryDate: null,
    });
  };

  const saveCertificationEdit = async () => {
    setShowEditValidation(true);
    if (
      !editingCertId ||
      !editCertification.name ||
      !editCertification.startDate
    )
      return;

    // isStarred has no UI control today; preserve whatever value already exists.
    const existingCertification = certifications.find((cert) => cert.id === editingCertId);

    const updatedCertification: CertificationViewModel = {
      id: editingCertId,
      startDate: toLocalDateString(editCertification.startDate),
      expiryDate: editCertification.expiryDate
        ? toLocalDateString(editCertification.expiryDate)
        : "",
      name: editCertification.name,
      isStarred: existingCertification?.isStarred ?? false,
    };

    if (personId) {
      setIsSaving(true);
      try {
        await personCertificationService.updateCertificationItem({
          id: parseInt(updatedCertification.id),
          // Backend contract uses legacy field name userId, but value is personId.
          userId: personId,
          name: updatedCertification.name,
          date: toUTCDate(editCertification.startDate),
          isStarred: updatedCertification.isStarred,
          expirationDate: editCertification.expiryDate ? toUTCDate(editCertification.expiryDate) : null,
        } as Certification);

        onChange(
          certifications.map((cert) =>
            cert.id === editingCertId ? updatedCertification : cert
          )
        );

        notifications.show({
          title: "Success",
          message: "Certification updated successfully",
          color: "green",
        });
      } catch (error) {
        console.error("Error updating certification:", error);
        const backendMessage = (error as { responseBody?: { error?: string } })?.responseBody?.error;
        notifications.show({
          title: "Error",
          message: backendMessage ?? "Failed to update certification. Please try again.",
          color: "red",
        });
      } finally {
        setIsSaving(false);
      }
    } else {
      // Just update local state if personId is not provided (fallback)
      onChange(
        certifications.map((cert) =>
          cert.id === editingCertId ? updatedCertification : cert
        )
      );
    }

    cancelEditCertification();
  };

  const openDeleteConfirmation = (certification: CertificationViewModel) => {
    setCertificationToDelete(certification);
    setDeleteModalOpen(true);
  };

  const confirmDelete = () => {
    if (certificationToDelete) {
      handleRemoveCertification(certificationToDelete.id);
      setDeleteModalOpen(false);
    }
  };

  if (isLoading) {
    return (
      <CVSectionContainer sectionId="certifications" title="Certifications">
        <Stack align="center" gap="md" py="xl">
          <Loader size="md" />
          <Text size="sm" c="dimmed">
            Loading certifications...
          </Text>
        </Stack>
      </CVSectionContainer>
    );
  }

  return (
    <CVSectionContainer
      sectionId="certifications"
      title="Certifications"
      buttonText={isEditMode ? "Add Certification" : undefined}
      onAddClick={isEditMode ? () => setIsAddFormOpen(true) : undefined}
      instructions={isEditMode ? INSTRUCTIONS : undefined}
      faqPath="certificates"
      personId={personId}
      cvId={cvId}
      getSectionData={getCertificationsSectionData}
    >
      <ValidationResult section={ValidateableCvSections.Certifications} />

      {!certificateManagementEnabled && (
        <FeatureUnavailableBanner message="Adding and editing certificates is temporarily unavailable — this feature is being rebuilt. Your existing certificates are unaffected and still visible below." />
      )}

      {certifications.length === 0 && !isAddFormOpen && (
        <Text size="sm" c="dimmed" ta="center" py="md">
          No certifications added yet. Click "Add Certification" to add your
          first certification.
        </Text>
      )}

      <Collapse in={isAddFormOpen}>
        <Box
          mb="md"
          p="md"
          style={{
            border: `2px solid ${theme.colors.cyan[7]}`,
            borderRadius: theme.radius.sm,
            backgroundColor: isDark ? theme.colors.dark[6] : FormUIColor.ADD_FORM_BG,
          }}
        >
          <Stack gap="md">
            <Autocomplete
              label="Certification Name"
              placeholder="Type to search for certifications..."
              required
              value={newCertification.name}
              onChange={handleNewCertificateSelect}
              data={certificateOptions.map((cert) => ({
                value: cert.value,
                label: cert.label,
              }))}
              disabled={isDictionaryLoading}
              limit={15}
              rightSection={isDictionaryLoading ? <Loader size="xs" /> : null}
            />

            <MonthYearDateInput
              label="Issue Date"
              value={newCertification.startDate}
              onChange={(value) =>
                setNewCertification({ ...newCertification, startDate: value })
              }
              required
            />

            <MonthYearDateInput
              label="Expiry Date"
              value={newCertification.expiryDate}
              onChange={(value) =>
                setNewCertification({ ...newCertification, expiryDate: value })
              }
            />

            <Group justify="flex-end" gap="sm">
              {showAddValidation && (() => {
                const errors: string[] = [];
                if (!newCertification.name) errors.push('Certification name is required');
                if (!newCertification.startDate) errors.push('Issue date is required');
                return errors.length > 0 ? (
                  <Alert color="red" w="100%" py="xs">
                    <List size="sm" spacing={2}>
                      {errors.map((e) => <List.Item key={e}>{e}</List.Item>)}
                    </List>
                  </Alert>
                ) : null;
              })()}
              <Button
                variant="outline"
                leftSection={<XMarkIcon width={16} height={16} />}
                onClick={() => { setIsAddFormOpen(false); setShowAddValidation(false); }}
                disabled={isSaving}
              >
                Cancel
              </Button>
              <Tooltip
                label="Adding and editing certificates is temporarily unavailable — this feature is being rebuilt."
                disabled={certificateManagementEnabled}
              >
                <Button
                  leftSection={<CheckIcon width={16} height={16} />}
                  onClick={handleAddCertification}
                  loading={isSaving}
                  disabled={!certificateManagementEnabled}
                >
                  Add Certification
                </Button>
              </Tooltip>
            </Group>
          </Stack>
        </Box>
      </Collapse>

      {certifications.map((cert) => (
        <React.Fragment key={cert.id}>
          {editingCertId === cert.id ? (
            // Show edit form instead of the card when editing
            <Box
              mb="md"
              p="md"
              style={{
                border: `2px solid ${theme.colors.blue[5]}`,
                borderRadius: theme.radius.sm,
                backgroundColor: isDark ? theme.colors.dark[6] : theme.colors.blue[0],
              }}
            >
              <Text size="sm" c="blue" fw={500} mb="md">
                Editing: {cert.name}
              </Text>
              <Stack gap="md">
                <Autocomplete
                  label="Certification Name"
                  placeholder="Type to search for certifications..."
                  required
                  value={editCertification.name}
                  onChange={handleEditCertificateSelect}
                  data={certificateOptions.map((cert) => ({
                    value: cert.value,
                    label: cert.label,
                  }))}
                  disabled={isDictionaryLoading}
                  limit={15}
                  rightSection={
                    isDictionaryLoading ? <Loader size="xs" /> : null
                  }
                />

                <MonthYearDateInput
                  label="Issue Date"
                  value={editCertification.startDate}
                  onChange={(value) =>
                    setEditCertification({
                      ...editCertification,
                      startDate: value,
                    })
                  }
                  required
                />

                <MonthYearDateInput
                  label="Expiry Date"
                  value={editCertification.expiryDate}
                  onChange={(value) =>
                    setEditCertification({
                      ...editCertification,
                      expiryDate: value,
                    })
                  }
                />

                <Group justify="flex-end" gap="sm">
                  {showEditValidation && (() => {
                    const errors: string[] = [];
                    if (!editCertification.name) errors.push('Certification name is required');
                    if (!editCertification.startDate) errors.push('Issue date is required');
                    return errors.length > 0 ? (
                      <Alert color="red" w="100%" py="xs">
                        <List size="sm" spacing={2}>
                          {errors.map((e) => <List.Item key={e}>{e}</List.Item>)}
                        </List>
                      </Alert>
                    ) : null;
                  })()}
                  <Button
                    variant="outline"
                    leftSection={<XMarkIcon width={16} height={16} />}
                    onClick={cancelEditCertification}
                    disabled={isSaving}
                  >
                    Cancel
                  </Button>
                  <Tooltip
                    label="Adding and editing certificates is temporarily unavailable — this feature is being rebuilt."
                    disabled={certificateManagementEnabled}
                  >
                    <Button
                      leftSection={<CheckIcon width={16} height={16} />}
                      onClick={saveCertificationEdit}
                      loading={isSaving}
                      disabled={!certificateManagementEnabled}
                    >
                      Save
                    </Button>
                  </Tooltip>
                </Group>
              </Stack>
            </Box>
          ) : (
            // Show the normal card when not editing
            <CVItemCard
              title={cert.name}
              dates={formatDateRange(cert.startDate, cert.expiryDate)}
              onEdit={() => handleEditCertification(cert)}
              onDelete={() => openDeleteConfirmation(cert)}
              isEditMode={isEditMode}
            />
          )}
        </React.Fragment>
      ))}

      <DeleteConfirmationModal
        opened={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={confirmDelete}
        itemName={certificationToDelete?.name}
        isLoading={isSaving}
      />
    </CVSectionContainer>
  );
}
